import { motion } from 'framer-motion'
import { ArrowRight, Shield, Crown, Lock, Sparkles, Users, Globe } from 'lucide-react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const Home = () => {
  const services = [
    {
      icon: Crown,
      title: 'White Glove Service',
      description: 'Premium technology support with meticulous attention to every detail and your complete satisfaction.',
      path: '/services/white-glove-service'
    },
    {
      icon: Shield,
      title: 'Managed Security',
      description: 'Military-grade protection for your digital life across all devices, networks, and properties.',
      path: '/services/managed-security'
    },
    {
      icon: Users,
      title: 'Technology Concierge',
      description: 'Your  personal technology advisor, available 24/7 for expert guidance and immediate support.',
      path: '/services/technology-concierge'
    },
    {
      icon: Sparkles,
      title: 'Technology Magic',
      description: 'Breakthrough innovations and bespoke solutions that transform the impossible into reality.',
      path: '/services/technology-magic'
    },
  ]

  return (
    <>
      <SEO 
        description="Praetorian Works - Elite technology services for High Net Worth and Ultra High Net Worth individuals. White glove support, enterprise security, and technology concierge services from Disruption Consulting."
        keywords="high net worth technology, UHNW technology services, private technology concierge, elite IT support, luxury technology services, white glove tech support"
        canonical="/"
      />
      
      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-black/30">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary-900/10 via-transparent to-transparent"></div>
          </div>
          
          <div className="container-max relative z-10">
            <div className="text-center space-y-8 max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-4 shadow-2xl"
              >
                <Shield className="w-12 h-12 text-secondary-900" />
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight"
              >
                <span className="text-white">Elite Technology</span>{' '}
                <span className="text-primary-500">Services</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl md:text-2xl text-neutral-300 max-w-4xl mx-auto leading-relaxed"
              >
                Praetorian Works delivers white glove technology services for discerning High Net Worth 
                and Ultra High Net Worth clientele. Enterprise-class security, 24/7 expert support, and 
                bespoke solutions worthy of your status.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4"
              >
                <Link to="/contact" className="btn-primary group text-lg">
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link to="/services" className="btn-secondary text-lg">
                  Explore Services
                </Link>
              </motion.div>
            </div>
          </div>
          
          {/* Elegant Background Elements */}
          <div className="absolute top-1/4 left-10 w-32 h-32 bg-primary-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-20 w-48 h-48 bg-primary-600/10 rounded-full blur-3xl"></div>
        </section>

        {/* Value Propositions */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-2">
                  <Lock className="w-8 h-8 text-secondary-900" />
                </div>
                <h3 className="text-2xl font-bold text-white">Complete Discretion</h3>
                <p className="text-neutral-400">
                  Your privacy is paramount. All services delivered with absolute confidentiality and security.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-2">
                  <Crown className="w-8 h-8 text-secondary-900" />
                </div>
                <h3 className="text-2xl font-bold text-white">VIP Treatment</h3>
                <p className="text-neutral-400">
                  White glove service tailored to your preferences with dedicated expert teams.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-2">
                  <Globe className="w-8 h-8 text-secondary-900" />
                </div>
                <h3 className="text-2xl font-bold text-white">Always Available</h3>
                <p className="text-neutral-400">
                  24/7/365 support wherever you are—homes, yachts, aircraft, and everywhere between.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Preview */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <div className="text-center space-y-6 mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white"
              >
                Exceptional <span className="text-primary-500">Services</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-lg text-neutral-300 max-w-3xl mx-auto"
              >
                From comprehensive security to innovative solutions, our elite services are designed 
                to simplify your digital life and protect what matters most.
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-secondary-800 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center mb-6 group-hover:shadow-lg group-hover:shadow-primary-500/50 transition-all duration-300">
                    <service.icon className="w-7 h-7 text-secondary-900" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-3">
                    {service.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link 
                    to={service.path}
                    className="inline-flex items-center text-primary-500 hover:text-primary-400 font-medium transition-colors duration-200"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Link to="/services" className="btn-primary group text-lg">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Backed by Disruption Consulting */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-white"
              >
                Powered by <span className="text-primary-500">Disruption Consulting</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-lg text-neutral-300 leading-relaxed"
              >
                Praetorian Works is an offering from Disruption Consulting, a firm with over 30 years 
                of experience delivering enterprise-class technology solutions. We bring decades of expertise 
                in security, infrastructure, and innovation to serve the unique needs of our distinguished clientele.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <Link to="/about" className="btn-secondary text-lg">
                  Learn About Us
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding relative overflow-hidden">
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Experience Technology Excellence
              </h2>
              <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
                Discover how Praetorian Works can elevate your digital life with elite services 
                designed exclusively for discerning individuals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                <Link to="/contact" className="btn-primary text-lg">
                  Schedule Consultation
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Home
